import { useEffect, useState } from 'react';
import { api, type MapConfig } from '../api/endpoints';

/**
 * Настройки карты (`GET /web/map-config`) — один запрос на вкладку.
 *
 * Ключ и центр не меняются за время работы с кабинетом, а карта есть на нескольких
 * панелях: перечитывать настройки на каждое открытие панели незачем. Ошибка не
 * кэшируется — следующая панель спросит снова.
 *
 * `unavailable` — сервер настройки не отдал (старый сервер без операции, сбой сети):
 * экран ведёт себя как без ключа, форма остаётся рабочей без карты.
 */

export type MapConfigState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly config: MapConfig }
  | { readonly status: 'unavailable' };

let cached: Promise<MapConfig> | null = null;

export function useMapConfig(): MapConfigState {
  const [state, setState] = useState<MapConfigState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    cached ??= api.mapConfig();

    const current = cached;

    current
      .then((config) => {
        if (!cancelled) {
          setState({ status: 'ready', config });
        }
      })
      .catch(() => {
        if (cached === current) {
          cached = null;
        }

        if (!cancelled) {
          setState({ status: 'unavailable' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Только для тестов. */
export function resetMapConfigForTests(): void {
  cached = null;
}
